import { useState, useCallback } from 'react';

export const useVrMindMapApi = (projectId, baseUrl) => {
  const [assets, setAssets] = useState({
    mesh: [],
    video: [],
    audio: [],
    image: [],
    text: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${baseUrl}/api/v1/assets/project/${projectId}/assets-previews`
      );
      const data = await response.json();

      if (data.success) {
        setAssets({
          mesh: data.data.mesh || [],
          video: data.data.video || [],
          audio: data.data.audio || [],
          image: data.data.image || [],
          text: data.data.text || [],
        });
        return data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching assets:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [projectId, baseUrl]);

  const fetchAssetDetails = useCallback(
    async (assetId) => {
      try {
        const response = await fetch(`${baseUrl}/api/v1/assets/${assetId}`);
        const data = await response.json();

        if (data.success && data.data) {
          return data.data;
        }
        return null;
      } catch (error) {
        console.error('Error fetching asset details:', error);
        return null;
      }
    },
    [baseUrl]
  );

  const updateNodeValue = useCallback(
    async (node, editValue, selectedAsset) => {
      if (!selectedAsset) return false;

      try {
        setSaving(true);

        if (node.eventId) {
          const event = selectedAsset.events?.find(
            (e) => e._id === node.eventId
          );

          if (!event) {
            alert('Event not found. Please refresh and try again.');
            return false;
          }

          const pathParts = node.path.split('.');
          const fieldName = pathParts[pathParts.length - 1];

          const updateData = {
            vr_trigger: event.vr_trigger || '',
            action: event.action || '',
            [fieldName]: editValue,
          };

          const endpoint = `${baseUrl}/api/v1/vr/events/${selectedAsset.id || selectedAsset._id}/${node.eventId}`;

          const response = await fetch(endpoint, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updateData),
          });

          const data = await response.json();

          if (data.success || response.ok) {
            return true;
          } else {
            console.error('Failed to update:', data);
            alert('Failed to update property. Please try again.');
            return false;
          }
        } else {
          // Handle special cases for VR manipulation APIs that require complete payloads
          let endpoint;
          let updateData;

          if (node.apiPath === 'manipulations') {
            endpoint = `${baseUrl}/api/v1/vr/manipulations/${selectedAsset.id || selectedAsset._id}`;

            const pathParts = node.path.split('.');
            const fieldName = pathParts[pathParts.length - 1];
            const currentManipulation = selectedAsset.vr_manipulation || {};
            const currentGrabSettings = currentManipulation.grab_settings || {};
            const currentCustomOffset =
              currentGrabSettings.custom_attach_offset || {};

            updateData = {
              can_grab: currentManipulation.can_grab ?? true,
              can_move: currentManipulation.can_move ?? true,
              can_rotate: currentManipulation.can_rotate ?? true,
              can_scale: currentManipulation.can_scale ?? true,
              manipulation_mode:
                currentManipulation.manipulation_mode || 'standard',
              grab_settings: {
                grab_type: currentGrabSettings.grab_type || 'snap',
                two_hand_grab: currentGrabSettings.two_hand_grab ?? true,
                attach_point:
                  currentGrabSettings.attach_point || 'contact_point',
                custom_attach_offset: {
                  x: currentCustomOffset.x ?? 0,
                  y: currentCustomOffset.y ?? 0,
                  z: currentCustomOffset.z ?? 0,
                },
                throw_multiplier: currentGrabSettings.throw_multiplier ?? 1.5,
                angular_throw_multiplier:
                  currentGrabSettings.angular_throw_multiplier ?? 1,
              },
            };

            if (
              [
                'can_grab',
                'can_move',
                'can_rotate',
                'can_scale',
                'manipulation_mode',
              ].includes(fieldName)
            ) {
              updateData[fieldName] = editValue;
            } else if (pathParts.includes('grab_settings')) {
              const grabSettingsIndex = pathParts.indexOf('grab_settings');
              const grabField = pathParts[grabSettingsIndex + 1];

              if (grabField === 'custom_attach_offset') {
                const axis = pathParts[pathParts.length - 1];
                updateData.grab_settings.custom_attach_offset[axis] = editValue;
              } else {
                updateData.grab_settings[grabField] = editValue;
              }
            }
          } else if (node.apiPath === 'manipulations/constraints/movement') {
            endpoint = `${baseUrl}/api/v1/vr/manipulations/${selectedAsset.id || selectedAsset._id}/constraints/movement`;

            // Transform to flat structure expected by API
            const pathParts = node.path.split('.');
            const fieldName = pathParts[pathParts.length - 1];

            // Get current movement constraints from asset to preserve other fields
            const currentConstraints =
              selectedAsset.vr_manipulation?.movement_constraints || {};

            updateData = {
              lock_x: currentConstraints.lock_x ?? false,
              lock_y: currentConstraints.lock_y ?? false,
              lock_z: currentConstraints.lock_z ?? false,
              snap_to_grid: currentConstraints.snap_to_grid ?? false,
              grid_size: currentConstraints.grid_size ?? 0.5,
              movement_range: {
                min_x: currentConstraints.movement_range?.min_x ?? null,
                max_x: currentConstraints.movement_range?.max_x ?? null,
                min_y: currentConstraints.movement_range?.min_y ?? null,
                max_y: currentConstraints.movement_range?.max_y ?? null,
                min_z: currentConstraints.movement_range?.min_z ?? null,
                max_z: currentConstraints.movement_range?.max_z ?? null,
              },
            };

            // Update the specific field
            if (
              fieldName === 'lock_x' ||
              fieldName === 'lock_y' ||
              fieldName === 'lock_z' ||
              fieldName === 'snap_to_grid' ||
              fieldName === 'grid_size'
            ) {
              updateData[fieldName] = editValue;
            } else if (pathParts.includes('movement_range')) {
              // Handle movement_range fields (min_x, max_x, min_y, max_y, min_z, max_z)
              updateData.movement_range[fieldName] = editValue;
            }
          } else if (node.apiPath === 'manipulations/constraints/rotation') {
            endpoint = `${baseUrl}/api/v1/vr/manipulations/${selectedAsset.id || selectedAsset._id}/constraints/rotation`;

            // Transform to flat structure expected by API
            const pathParts = node.path.split('.');
            const fieldName = pathParts[pathParts.length - 1];

            // Get current rotation constraints from asset to preserve other fields
            const currentConstraints =
              selectedAsset.vr_manipulation?.rotation_constraints || {};

            updateData = {
              lock_x: currentConstraints.lock_x ?? false,
              lock_y: currentConstraints.lock_y ?? false,
              lock_z: currentConstraints.lock_z ?? false,
              rotation_mode: currentConstraints.rotation_mode || 'free',
              snap_angle: currentConstraints.snap_angle ?? 15,
              fixed_angles: currentConstraints.fixed_angles || [],
            };

            // Update the specific field
            if (
              [
                'lock_x',
                'lock_y',
                'lock_z',
                'rotation_mode',
                'snap_angle',
              ].includes(fieldName)
            ) {
              updateData[fieldName] = editValue;
            }
          } else if (node.apiPath === 'manipulations/constraints/scale') {
            endpoint = `${baseUrl}/api/v1/vr/manipulations/${selectedAsset.id || selectedAsset._id}/constraints/scale`;

            // Transform to flat structure expected by API
            const pathParts = node.path.split('.');
            const fieldName = pathParts[pathParts.length - 1];

            // Get current scale constraints from asset to preserve other fields
            const currentConstraints =
              selectedAsset.vr_manipulation?.scale_constraints || {};

            updateData = {
              uniform_scale: currentConstraints.uniform_scale ?? true,
              maintain_aspect_ratio:
                currentConstraints.maintain_aspect_ratio ?? true,
              scale_sensitivity: currentConstraints.scale_sensitivity ?? 1,
              min_scale: {
                x: currentConstraints.min_scale?.x ?? 0.1,
                y: currentConstraints.min_scale?.y ?? 0.1,
                z: currentConstraints.min_scale?.z ?? 0.1,
              },
              max_scale: {
                x: currentConstraints.max_scale?.x ?? 10,
                y: currentConstraints.max_scale?.y ?? 10,
                z: currentConstraints.max_scale?.z ?? 10,
              },
            };

            // Update the specific field
            if (
              fieldName === 'uniform_scale' ||
              fieldName === 'maintain_aspect_ratio' ||
              fieldName === 'scale_sensitivity'
            ) {
              updateData[fieldName] = editValue;
            } else if (
              fieldName.startsWith('min_scale.') ||
              fieldName.startsWith('max_scale.')
            ) {
              // Handle min_scale and max_scale fields
              const [scaleType, axis] = fieldName.split('.');
              updateData[scaleType][axis] = editValue;
            } else if (
              pathParts.includes('min_scale') ||
              pathParts.includes('max_scale')
            ) {
              // Handle nested path like vr_manipulation.scale_constraints.min_scale.x
              const scaleTypeIndex = pathParts.findIndex(
                (p) => p === 'min_scale' || p === 'max_scale'
              );
              if (scaleTypeIndex !== -1) {
                const scaleType = pathParts[scaleTypeIndex];
                const axis = pathParts[pathParts.length - 1];
                updateData[scaleType][axis] = editValue;
              }
            }
          } else {
            // Default behavior for other APIs
            endpoint = `${baseUrl}/api/v1/vr/${node.apiPath}/${selectedAsset.id || selectedAsset._id}`;

            const pathParts = node.path.split('.');
            updateData = {};

            let current = updateData;
            for (let i = 0; i < pathParts.length - 1; i++) {
              current[pathParts[i]] = {};
              current = current[pathParts[i]];
            }
            current[pathParts[pathParts.length - 1]] = editValue;
          }

          const response = await fetch(endpoint, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updateData),
          });

          const data = await response.json();

          if (data.success || response.ok) {
            return true;
          } else {
            console.error('Failed to update:', data);
            alert('Failed to update property. Please try again.');
            return false;
          }
        }
      } catch (error) {
        console.error('Error updating node value:', error);
        alert('Error updating property. Please check console for details.');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [baseUrl]
  );

  const handleTriggerSelection = useCallback(
    async (triggerValue, selectedAsset) => {
      if (!selectedAsset || !triggerValue) return false;

      try {
        setSaving(true);
        const cleanedData = { vr_trigger: triggerValue };

        const endpoint = `${baseUrl}/api/v1/vr/events/${selectedAsset.id || selectedAsset._id}`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(cleanedData),
        });

        const data = await response.json();
        if (data.success || response.ok) {
          return true;
        } else {
          console.error('Failed to create event:', data);
          alert('Failed to create event. Please try again.');
          return false;
        }
      } catch (error) {
        console.error('Error creating event:', error);
        alert('Error creating event. Please check console for details.');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [baseUrl]
  );

  const handleEventSubmit = useCallback(
    async (
      eventMode,
      eventForm,
      selectedAsset,
      pendingEventId,
      currentEvent
    ) => {
      if (!selectedAsset) return false;

      try {
        setSaving(true);

        const cleanedData = {};

        if (eventMode === 'add-trigger') {
          if (!eventForm.vr_trigger) {
            alert('Please select a trigger');
            return false;
          }
          cleanedData.vr_trigger = eventForm.vr_trigger;

          const endpoint = `${baseUrl}/api/v1/vr/events/${selectedAsset.id || selectedAsset._id}`;
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(cleanedData),
          });

          const data = await response.json();
          if (data.success || response.ok) {
            return true;
          } else {
            console.error('Failed to create event:', data);
            alert('Failed to create event. Please try again.');
            return false;
          }
        } else if (eventMode === 'add-action') {
          if (!eventForm.action) {
            alert('Please enter an action');
            return false;
          }
          cleanedData.action = eventForm.action;
          if (eventForm.vr_trigger) {
            cleanedData.vr_trigger = eventForm.vr_trigger;
          }

          const endpoint = `${baseUrl}/api/v1/vr/events/${selectedAsset.id || selectedAsset._id}/${pendingEventId}`;
          const response = await fetch(endpoint, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(cleanedData),
          });

          const data = await response.json();
          if (data.success || response.ok) {
            return true;
          } else {
            console.error('Failed to update event:', data);
            alert('Failed to update event. Please try again.');
            return false;
          }
        } else if (eventMode === 'edit') {
          if (eventForm.vr_trigger)
            cleanedData.vr_trigger = eventForm.vr_trigger;
          if (eventForm.action) cleanedData.action = eventForm.action;

          const endpoint = `${baseUrl}/api/v1/vr/events/${selectedAsset.id || selectedAsset._id}/${currentEvent._id}`;
          const response = await fetch(endpoint, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(cleanedData),
          });

          const data = await response.json();
          if (data.success || response.ok) {
            return true;
          } else {
            console.error('Failed to save event:', data);
            alert('Failed to save event. Please try again.');
            return false;
          }
        }

        return false;
      } catch (error) {
        console.error('Error saving event:', error);
        alert('Error saving event. Please check console for details.');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [baseUrl]
  );

  const handleEventDelete = useCallback(
    async (currentEvent, selectedAsset) => {
      if (!selectedAsset || !currentEvent) return false;

      if (!confirm('Are you sure you want to delete this event?')) return false;

      try {
        setSaving(true);

        const endpoint = `${baseUrl}/api/v1/vr/events/${selectedAsset.id || selectedAsset._id}/${currentEvent._id}`;

        const response = await fetch(endpoint, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        if (data.success || response.ok) {
          return true;
        } else {
          console.error('Failed to delete event:', data);
          alert('Failed to delete event. Please try again.');
          return false;
        }
      } catch (error) {
        console.error('Error deleting event:', error);
        alert('Error deleting event. Please check console for details.');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [baseUrl]
  );

  return {
    assets,
    loading,
    saving,
    setAssets,
    fetchAssets,
    fetchAssetDetails,
    updateNodeValue,
    handleTriggerSelection,
    handleEventSubmit,
    handleEventDelete,
  };
};
